import * as Sentry from '@sentry/browser';
import {EventBus} from "../../common/ts/events/EventBus";
import {CheckboxUniqueSelectClickEvent, FullPageModalOpenEvent, WhitelabelEvent} from "../../common/ts/events/Events";
import HipflatSerpCTAs from "./HipflatSerpCTAs";
import PageData from "../../common/ts/PageData";
import SerpPageData from "../../common/ts/serp/SerpPageData";
import {connect} from "../../common/ts/connect";
import {HipflatPagination} from "./common/HipflatPagination";
import {WebVitalsTracking} from "../../common/ts/WebVitalsTracking";
import FeatureFlags, {HIPFLAT_WEB_COMPONENT_FEATURE_FLAG} from "../../common/ts/FeatureFlags";
import {WlSubmit} from "../../common/ts/webcomponents/events/WlSubmit";
import {SerpMarketStatsChart} from "../../common/ts/components/charts/SerpMarketStatsChart";
import {MarketStatsStyles} from "../../common/ts/components/charts/MarketStatsChart";
import http from "../../common/ts/core/http";
import {MarketStatsTracking} from "../../common/ts/core/tracking/MarketStatsTracking";

class Serp {
    private readonly filtersBarForm: HTMLFormElement
    private featureFlags: FeatureFlags;
    private sortingButton: Sorting

    constructor(pageData: PageData) {
        WebVitalsTracking.init("Serp")
        Sentry.setTag("name", "UserListView");
        new FullScreenFilters();
        this.filtersBarForm = document.getElementById("filters-form_desktop") as HTMLFormElement;
        this.featureFlags = new FeatureFlags();
        this.sortingButton = new Sorting(this.submit.bind(this));
        HipflatSerpCTAs.init(pageData)
        HipflatPagination.init();
        this.filterItemClicked();
        this.marketStats();
        this.manageWebComponents();
        new MarketStatsTracking().init(pageData);
        http.init();
    }

    private marketStats() {
        const marketStatsStyles: MarketStatsStyles = {
            lineColor: '#FD6E6E',
            fillColor: '#FD6E6E1A',
            pointColor: '#FFFFFF',
            pointHoverColor: '#FD6E6E',
            axisBorderColor: '#8F939F',
            scalesLabelColor: '#8F939F',
            pointRadius: 8,
            tickPadding: 10,
            tooltipTitleFontSize: 14,
            tooltipBodyFontSize: 16,
            chartType: 'line',
            xAxisRotation: false,
            yAxisRotation : false,
            barThickness: undefined,
            barBorderRadius: undefined,
        };
        new SerpMarketStatsChart().init(marketStatsStyles);
    }

    private manageWebComponents() {

        this.filtersBarForm.querySelectorAll(".js_filter-component")
            .forEach((element: Node) => (element as HTMLElement).addEventListener(WlSubmit.type, this.submit.bind(this)))

        if (this.filtersBarForm == null) {
            return
        }
        if (this.featureFlags.isActiveFeature(HIPFLAT_WEB_COMPONENT_FEATURE_FLAG)) {
            document.querySelectorAll("wl-dropdown, wl-input-dropdown").forEach((element: Element) => {
                element.addEventListener("change", (event: Event) => {
                    const target = event?.target as HTMLInputElement
                    if(target == null) {
                        return
                    }
                    if (target.classList.contains("geo-filter")) {
                        let geoField = this.filtersBarForm.querySelector("#hidden-geo-id") as HTMLInputElement | null
                        if (geoField != null) {
                            geoField.value = target.value || "";
                        }
                    }
                    this.submit()

                })
            })
        }

    }

    private submit() {
        let hidden = this.filtersBarForm.querySelector("#js-hidden-sorting") as HTMLInputElement
        if(hidden && this.sortingButton.getValue() != undefined) {
            // @ts-ignore
            hidden.value = this.sortingButton.getValue()
        }
        this.filtersBarForm.submit();
    }

    private filterItemClicked() {
        if (this.filtersBarForm == null) {
            return
        }

        let eventBus = EventBus.getInstance();
        eventBus.subscribe(CheckboxUniqueSelectClickEvent.TYPE, (event: WhitelabelEvent) => {
            const clickEvent: CheckboxUniqueSelectClickEvent = event as CheckboxUniqueSelectClickEvent;
            let name = clickEvent.payload.inputName;

            let geoField = this.filtersBarForm.querySelector("#hidden-geo-id") as HTMLInputElement | null
            let poiFieldChecked = this.filtersBarForm.querySelector("input[name='poi-token']:checked") as HTMLInputElement | null
            const inputIsChecked = clickEvent.payload.inputChecked;
            if (geoField && this.isPoiAction(name) && inputIsChecked) {
                this.assignProvinceToGeoFilter(geoField);
            } else if (geoField && this.isDistrictAction(name)) {
                if (inputIsChecked) {
                    this.assignInputCheckedToGeoFilter(geoField, clickEvent);
                    this.unassignPoiFilter(poiFieldChecked);
                } else {
                    this.assignProvinceToGeoFilter(geoField);
                }
            } else if (geoField && this.isProvinceAction(name)) {
                if (inputIsChecked) {
                    this.assignInputCheckedToGeoFilter(geoField, clickEvent);
                } else {
                    this.clearGeoFilter(geoField);
                }
                this.unassignPoiFilter(poiFieldChecked);
            }
            this.filtersBarForm.submit();
        });

    }
    private isProvinceAction(name: string) {
        return name == "hidden-geo-id_2";
    }

    private isDistrictAction(name: string) {
        return name == "hidden-geo-id_3";
    }

    private isPoiAction(name: string) {
        return name == "poi-token";
    }

    private clearGeoFilter(geoField: HTMLInputElement) {
        geoField.value = "";
    }

    private assignInputCheckedToGeoFilter(geoField: HTMLInputElement, clickEvent: CheckboxUniqueSelectClickEvent) {
        geoField.value = clickEvent.payload.inputValue;
    }

    private unassignPoiFilter(poiFieldChecked: HTMLInputElement | null) {
        if (poiFieldChecked) {
            poiFieldChecked.checked = false;
        }
    }

    private assignProvinceToGeoFilter(geoField: HTMLInputElement) {
        geoField.value = (document.querySelector('input[name="hidden-geo-id_2"]:checked') as HTMLInputElement).value;
    }
}

class Sorting {
    private select = document.getElementById('js-sort-button')?.querySelector('select')

    getValue() {
        return this.select?.value
    }

    constructor(onChange: CallableFunction) {
        this.select?.addEventListener("change", (event) => onChange(event))
    }
}

class FullScreenFilters {
    private readonly eventBus: EventBus;
    private openTriggers = Array.from(document.getElementsByClassName("js-full-screen-filters-trigger"));

    constructor() {
        this.eventBus = EventBus.getInstance();
        this.openTriggers.forEach((it) => it.addEventListener('click', () => this.open()));
    }

    open() {
        this.eventBus.emit(new FullPageModalOpenEvent({id: 'full-screen-filters-modal'}));
    }
}

(window as any).initSerp = (serpPageData: SerpPageData) => {
    connect(() => {
        new Serp(
            {
                viewPhoneModalField: "",
                viewWhatsappPhoneModalField: "",
                pageViewType: serpPageData.pageViewType,
                id: "",
                proppitId: "",
                operationType: "",
                userId: serpPageData.userId,
                pageViewId: serpPageData.pageViewId,
                brand: serpPageData.brand,
                country: serpPageData.country,
                device: serpPageData.device,
                chatsData: {
                    socialNetworks: [],
                    countryLanguage: "",
                    branId: 0,
                    adUrl: "",
                    userAgent: "",
                    ipAddress: "",
                    origin: 0
                },
                mapData: {
                    showMap: false,
                    adLocationData: {
                        coordinates: {
                            latitude: "",
                            longitude: ""
                        },
                        province: "",
                        locality: "",
                        district: "",
                        address: "",
                        postcode: ""
                    },
                    visibility: ""
                },
                contactCalendarData: {
                    daysOfWeekShort: "",
                    firstHourRange: "",
                    scheduledVisitString: "",
                    secondHourRange: "",
                    thirdHourRange: "",
                    yearMonths: ""
                },
                floorPlans: [],
                virtualTours: [],
                onlyFormContact: false,
                isBoosted: false,
                agencyPhone: "",
                phoneMandatory: serpPageData.phoneMandatory,
                emailMandatory: serpPageData.emailMandatory
            },
        );
    });
}
